import React, { useEffect, useRef, useState } from "react";
import { qrEventsApi } from "../../api/qrEventsApi";

// MUI
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import { notify } from "../../../../util/toast";
import { formatDateTime } from "../../../../util/dateUtils";
import { formatPhoneDisplay } from "../../../../util/phone";
import { ds } from "../../../../styles/designSystem";
import FormField from "../../../../component/common/FormField";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";
import ActionButton from "../../../../component/common/ActionButton";
import TablePaginator, { TableSizeSelect } from "../../../../component/common/TablePaginator";

// 한 페이지에 보여줄 기록 수 — 참가자 관리 표와 같은 선택지
const PAGE_SIZE_OPTIONS = [100, 300, 500, 1000, 2000, 3000];

/** 입장 → 마지막 감지까지 머문 시간. 한 번만 찍혔으면 "-" */
const formatStay = (joinTime, leaveTime) => {
    if (!joinTime || !leaveTime) return "-";
    const totalSec = Math.max(0, Math.floor((new Date(leaveTime) - new Date(joinTime)) / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    if (h > 0) return `${h}시간 ${m}분`;
    if (m > 0) return `${m}분`;
    return `${totalSec}초`;
};

/**
 * 입출입(부스) 확인 — 행사를 고르고, 참가자를 검색하거나 구역을 골라 방문 기록을 본다.
 *
 * - 구역 칩은 그 행사 출석 기록에 찍힌 구역 전부. 여러 개 고르면 그중 한 곳이라도 간 기록.
 * - 표는 방문 1건 = 1행, 이름 → 입장 시간 순이라 한 사람을 검색하면 간 구역이 순서대로 나온다.
 * - 행사 선택은 참가자 관리 탭과 공유한다 (selectedEventFromParent / onSelectedEventChange).
 */
export default function QrEventAttendanceBoard({
                                                   selectedEventFromParent = "",
                                                   onSelectedEventChange,
                                               }) {
    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState("");

    const [sections, setSections] = useState([]);                 // [{ sectionName, visitorCount }]
    const [selectedSections, setSelectedSections] = useState([]); // 고른 구역 이름 — 비면 전체
    const [keyword, setKeyword] = useState("");

    const [records, setRecords] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // 늦게 도착한 옛 응답이 새 결과를 덮지 않게
    const requestSeqRef = useRef(0);

    useEffect(() => {
        (async () => {
            setLoadingEvents(true);
            try {
                const response = await qrEventsApi.getEvents();
                setEvents(Array.isArray(response) ? response : []);
            } catch (error) {
                notify(error.message || "행사 목록을 가져오는 중 오류가 발생했습니다.", "error");
            } finally {
                setLoadingEvents(false);
            }
        })();
    }, []);

    const selectEvent = (name) => {
        setSelectedEvent(name);
        setSelectedSections([]);
        setPage(0);
        onSelectedEventChange?.(name);
    };

    // ✅ 참가자 관리 탭에서 고른 행사를 그대로 이어받는다
    useEffect(() => {
        if (!selectedEventFromParent || selectedEventFromParent === selectedEvent) return;
        selectEvent(selectedEventFromParent);
        // eslint-disable-next-line
    }, [selectedEventFromParent]);

    const loadSections = async (eventName) => {
        try {
            const response = await qrEventsApi.getAttendanceSections(eventName);
            setSections(Array.isArray(response) ? response : []);
        } catch (error) {
            setSections([]);
            notify(error.message || "구역 목록을 가져오는 중 오류가 발생했습니다.", "error");
        }
    };

    const loadRecords = async (eventName, query) => {
        const seq = ++requestSeqRef.current;
        setLoading(true);
        try {
            const response = await qrEventsApi.getAttendanceRecords(eventName, query);
            if (seq !== requestSeqRef.current) return;
            setRecords(response?.content ?? []);
            setTotalPages(response?.totalPages ?? 1);
            setTotalElements(response?.totalElements ?? 0);
        } catch (error) {
            if (seq !== requestSeqRef.current) return;
            setRecords([]);
            notify(error.message || "입출입 기록을 가져오는 중 오류가 발생했습니다.", "error");
        } finally {
            if (seq === requestSeqRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedEvent) return;
        loadSections(selectedEvent);
    }, [selectedEvent]);

    // 행사·구역·페이지·검색어가 바뀌면 다시 조회 — 검색어 타이핑 중엔 입력이 멈출 때까지 기다린다
    useEffect(() => {
        if (!selectedEvent) return;
        const t = setTimeout(() => {
            loadRecords(selectedEvent, { keyword, sections: selectedSections, page, size: pageSize });
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [selectedEvent, selectedSections, page, pageSize, keyword]);

    const toggleSection = (name) => {
        setSelectedSections((prev) => (prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]));
        setPage(0);
    };

    const refresh = () => {
        if (!selectedEvent) return;
        loadSections(selectedEvent);
        loadRecords(selectedEvent, { keyword, sections: selectedSections, page, size: pageSize });
    };

    // 화면에 걸린 조건(검색어·구역) 그대로 기록 전체를 받는다 — 지금 보이는 페이지만이 아니다
    const handleDownload = async () => {
        if (!selectedEvent || downloading) return;
        setDownloading(true);
        try {
            const res = await qrEventsApi.downloadAttendanceRecords(selectedEvent, { keyword, sections: selectedSections });
            const blob = new Blob([res instanceof Blob ? res : (res?.data ?? res)], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${selectedEvent}_입출입.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("입출입 기록 다운로드 오류", error);
            notify("입출입 기록 다운로드 중 오류가 발생했습니다.", "error");
        } finally {
            setDownloading(false);
        }
    };

    const filtered = keyword.trim() || selectedSections.length > 0;

    return (
        <Box>
            {/* 상단 컨트롤 */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-end" }}>
                    <FormField label="행사 선택 (행사명 검색 가능)" required>
                        <AppAutocomplete
                            options={events}
                            value={events.find((e) => e.eventName === selectedEvent) || null}
                            onChange={(v) => {
                                const name = v && typeof v !== "string" ? v.eventName : "";
                                if (name) selectEvent(name);
                            }}
                            getOptionLabel={(o) => (typeof o === "string" ? o : (o?.eventName || ""))}
                            isOptionEqualToValue={(a, b) => {
                                const an = typeof a === "string" ? a : a?.eventName;
                                const bn = typeof b === "string" ? b : b?.eventName;
                                return an === bn;
                            }}
                            disabled={loadingEvents}
                            placeholder="행사를 선택하세요"
                            variant="outlined" disableUnderline={false}
                            sx={{ minWidth: 280 }}
                        />
                    </FormField>

                    <Typography sx={{ ...ds.typography.caption, pb: 0.8 }}>
                        선택된 행사: <b>{selectedEvent || "(미선택)"}</b>
                    </Typography>

                    {(loadingEvents || loading) && <CircularProgress size={22} />}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <ActionButton variant="download" onClick={handleDownload}
                        disabled={!selectedEvent} loading={downloading}
                        label="결과 xlsx 다운로드" />
                    <Tooltip title="새로고침">
                        <span>
                            <IconButton size="small" onClick={refresh} disabled={!selectedEvent}>
                                <RefreshRoundedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ ...ds.card, mt: 2, overflow: "hidden" }}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                        <Typography sx={ds.typography.sectionTitle}>입출입 기록</Typography>
                        <Box sx={{ flex: 1 }} />
                        <TextField
                            size="small"
                            placeholder="이름·소속·연락처·이메일·개인코드 검색"
                            value={keyword}
                            onChange={(e) => { setKeyword(e.target.value); setPage(0); }}
                            disabled={!selectedEvent}
                            sx={{ width: { xs: "100%", sm: 320 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: keyword ? (
                                    <InputAdornment position="end">
                                        <Tooltip title="검색어 지우기">
                                            <IconButton size="small"
                                                onClick={() => { setKeyword(""); setPage(0); }}
                                                sx={{ p: 0.5 }}>
                                                <ClearIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        {selectedEvent && (
                            <Typography sx={{ ...ds.typography.caption, whiteSpace: "nowrap" }}>
                                {totalElements}건
                            </Typography>
                        )}
                        {/* 크기를 바꾸면 첫 페이지부터 다시 */}
                        <TableSizeSelect value={pageSize} options={PAGE_SIZE_OPTIONS}
                            onChange={(size) => { setPageSize(size); setPage(0); }} />
                    </Stack>

                    {/* 구역 필터 — 출석 기록에 찍힌 구역 전부. 여러 개 고르면 그중 한 곳이라도 간 기록 */}
                    {selectedEvent && (
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.5 }}>
                            <Typography sx={{ ...ds.typography.caption, fontWeight: 700, mr: 0.5 }}>구역</Typography>
                            <Chip size="small" label="전체"
                                color={selectedSections.length === 0 ? "primary" : "default"}
                                variant={selectedSections.length === 0 ? "filled" : "outlined"}
                                onClick={() => { setSelectedSections([]); setPage(0); }} />
                            {sections.map((s) => {
                                const on = selectedSections.includes(s.sectionName);
                                return (
                                    <Chip key={s.sectionName} size="small"
                                        label={s.sectionName}
                                        color={on ? "primary" : "default"}
                                        variant={on ? "filled" : "outlined"}
                                        onClick={() => toggleSection(s.sectionName)} />
                                );
                            })}
                            {sections.length === 0 && (
                                <Typography sx={ds.typography.caption}>아직 태그된 구역이 없습니다.</Typography>
                            )}
                        </Stack>
                    )}
                </Box>
                <Divider />
                <TableContainer>
                    {/* 열 너비는 정해 두지 않는다 — 값 길이에 따라 정해지고, 줄바꿈 없이 넘치면 가로 스크롤 */}
                    <Table size="small" sx={[ds.tableSx, { "& th, & td": { whiteSpace: "nowrap" } }]}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">개인코드</TableCell>
                                <TableCell align="center">이름</TableCell>
                                <TableCell align="center">소속</TableCell>
                                <TableCell align="center">연락처</TableCell>
                                <TableCell align="center">구역</TableCell>
                                <TableCell align="center">입장</TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                        <span>마지막 감지</span>
                                        <Tooltip title="그 구역에서 마지막으로 태그한 시간이에요">
                                            <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                                <TableCell align="center">체류</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {records.map((r) => (
                                <TableRow key={`${r.participantId}-${r.sectionName}`} hover>
                                    <TableCell align="center">{r.privateCode}</TableCell>
                                    <TableCell align="center">{r.name}</TableCell>
                                    <TableCell align="center">{r.affiliation}</TableCell>
                                    <TableCell align="center">{formatPhoneDisplay(r.contactNumber)}</TableCell>
                                    <TableCell align="center">{r.sectionName}</TableCell>
                                    <TableCell align="center">{formatDateTime(r.joinTime)}</TableCell>
                                    <TableCell align="center">{r.leaveTime ? formatDateTime(r.leaveTime) : "-"}</TableCell>
                                    <TableCell align="center">{formatStay(r.joinTime, r.leaveTime)}</TableCell>
                                </TableRow>
                            ))}

                            {records.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                        {!selectedEvent
                                            ? "행사를 선택해주세요."
                                            : loading
                                                ? "불러오는 중..."
                                                : filtered
                                                    ? "조건에 맞는 입출입 기록이 없습니다."
                                                    : "아직 입출입 기록이 없습니다."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePaginator
                        totalPages={totalPages}
                        total={totalElements}
                        pageSize={pageSize}
                        page={page + 1}
                        onPageChange={(p) => setPage(p - 1)}
                    />
                </TableContainer>
            </Paper>
        </Box>
    );
}
